// tp-kit-items.js — "monte seu kit" da PDP.
//
// Custom element (e nao init no DOMContentLoaded) pelo mesmo motivo do
// quantity-bundle.js: o tp-sabores troca o innerHTML do <product-info> e
// reexecuta os scripts da secao. Como custom element o connectedCallback roda
// sozinho quando o elemento entra no DOM, e o guard abaixo evita a
// redeclaracao da classe no escopo global.
//
// Regra de negocio: trocar a variante (a "caixa") redistribui as unidades
// igualmente entre os itens, respeitando o estoque de cada um. A partir dai o
// cliente ajusta nos +/- a vontade — a soma NAO fica travada no total, mas
// nenhum item passa do proprio estoque.
//
// No carrinho, cada sabor vira uma linha propria. A variante da caixa nao e
// adicionada: ela e so o seletor de quantas unidades distribuir.

if (!customElements.get("tp-kit-items")) {
  class TpKitItems extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.items = Array.from(this.querySelectorAll(".tp-kit__item"));
      if (!this.items.length) return;

      this.initialized = true;

      this.counterCurrentEl = this.querySelector(".tp-kit__counter-current");
      this.counterTargetEl = this.querySelector(".tp-kit__counter-target");
      this.buyButton = this.querySelector(".tp-kit__buy");
      this.errorEl = this.querySelector(".tp-kit__error");

      this.unitsMap = this.parseUnitsMap();
      this.variantId = this.dataset.variantId || null;
      this.sizeIndex = parseInt(this.dataset.sizeIndex, 10);
      this.stockText = this.dataset.stockText || "";
      this.targetTotal = 0;

      // O bloco de quantidade e o kit sao blocos irmaos dentro do
      // <product-info>, entao o change do card sobe ate ele.
      this.scope = this.closest("product-info") || document;
      this.onOptionChange = this.handleOptionChange.bind(this);
      this.scope.addEventListener("change", this.onOptionChange);

      this.onStepClick = this.handleStepClick.bind(this);
      this.addEventListener("click", this.onStepClick);

      if (this.buyButton) {
        this.onBuyClick = this.addToCart.bind(this);
        this.buyButton.addEventListener("click", this.onBuyClick);
      }

      this.subscribeToVariantChange();

      this.distribute(this.readTotal());
    }

    disconnectedCallback() {
      this.scope?.removeEventListener("change", this.onOptionChange);
      this.buyButton?.removeEventListener("click", this.onBuyClick);
      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
        this.variantChangeUnsubscriber = null;
      }
    }

    parseUnitsMap() {
      try {
        return JSON.parse(this.dataset.unitsMap || "{}");
      } catch (err) {
        console.warn("[tp-kit-items] data-units-map invalido", err);
        return {};
      }
    }

    // 0 (ou ausente) no Liquid quer dizer "sem controle de estoque".
    maxFor(item) {
      if (item.hidden) return 0;
      const max = parseInt(item.dataset.maxQty, 10);
      return Number.isFinite(max) && max > 0 ? max : Infinity;
    }

    // "1 L", "1l" e "1L" tem que casar entre si.
    normalizeOption(value) {
      return String(value ?? "")
        .toLowerCase()
        .replace(/\s+/g, "");
    }

    // Troca a variante de cada linha para a que casa com o tamanho escolhido
    // no produto principal. Item sem aquele tamanho disponivel sai da lista:
    // cair na variante padrao mandaria outro tamanho no pedido.
    applySize(variant) {
      if (!Number.isFinite(this.sizeIndex) || this.sizeIndex < 0) return;

      const sizeKey = this.normalizeOption(variant?.options?.[this.sizeIndex]);
      if (!sizeKey) return;

      this.items.forEach((item) => {
        let candidates = [];
        try {
          candidates = JSON.parse(item.dataset.variants || "[]");
        } catch (err) {
          console.warn("[tp-kit-items] data-variants invalido", err);
        }

        const match = candidates.find((candidate) =>
          candidate.opts?.includes(sizeKey),
        );

        item.hidden = !match;
        item.dataset.variantId = match ? match.id : "";
        item.dataset.maxQty = match?.max ?? 0;
      });
    }

    // Ordem de preferencia: card de quantidade marcado > mapa da variante >
    // valor renderizado no Liquid.
    readTotal({ root, variantId } = {}) {
      const source = root || this.scope;
      const checked = source.querySelector?.(".tp-qty-variants__input:checked");
      const fromCard = parseInt(checked?.dataset.totalUnits ?? "", 10);
      if (Number.isFinite(fromCard) && fromCard > 0) return fromCard;

      const fromMap = parseInt(this.unitsMap[variantId ?? this.variantId] ?? "", 10);
      if (Number.isFinite(fromMap) && fromMap > 0) return fromMap;

      const fallback = parseInt(this.dataset.fallbackUnits || "", 10);
      return Number.isFinite(fallback) && fallback > 0 ? fallback : 0;
    }

    // Round-robin: da uma unidade por vez percorrendo a lista. Sai igual a
    // divisao com resto nos primeiros (36 em 8 itens = 5,5,5,5,4,4,4,4) e ja
    // pula sozinho quem bateu no teto de estoque, jogando a sobra em quem
    // ainda tem espaco.
    distribute(total) {
      this.targetTotal = total;

      const caps = this.items.map((item) => this.maxFor(item));
      const quantities = new Array(this.items.length).fill(0);

      let remaining = total;
      let placedInPass;

      do {
        placedInPass = 0;
        for (let i = 0; i < this.items.length && remaining > 0; i += 1) {
          if (quantities[i] >= caps[i]) continue;
          quantities[i] += 1;
          remaining -= 1;
          placedInPass += 1;
        }
      } while (remaining > 0 && placedInPass > 0);

      this.items.forEach((item, index) => {
        this.setItemQuantity(item, quantities[index]);
      });

      if (this.counterTargetEl) this.counterTargetEl.textContent = total;
      this.updateCounter();
    }

    setItemQuantity(item, quantity) {
      const max = this.maxFor(item);
      const value = Math.min(max, Math.max(0, Math.round(quantity) || 0));

      item.dataset.qty = value;

      const label = item.querySelector(".tp-kit__item-qty");
      if (label) label.textContent = value;

      const minus = item.querySelector(".tp-kit__step--minus");
      if (minus) minus.disabled = value === 0;

      const maxed = value >= max;

      const plus = item.querySelector(".tp-kit__step--plus");
      if (plus) plus.disabled = maxed;

      // O aviso so aparece quando o + trava por estoque — e a explicacao de
      // por que travou. Preenchido aqui porque o numero muda junto com a
      // variante quando o tamanho e trocado.
      const stock = item.querySelector(".tp-kit__item-stock");
      if (stock) {
        const showStock = maxed && Number.isFinite(max);
        if (showStock) {
          stock.textContent = this.stockText.replace("{estoque}", max);
        }
        stock.hidden = !showStock;
      }

      item.classList.toggle("tp-kit__item--maxed", maxed);
    }

    currentTotal() {
      return this.items.reduce(
        (sum, item) => sum + (parseInt(item.dataset.qty, 10) || 0),
        0,
      );
    }

    updateCounter() {
      const current = this.currentTotal();

      if (this.counterCurrentEl) this.counterCurrentEl.textContent = current;
      this.classList.toggle("tp-kit--complete", current === this.targetTotal);

      // Sem nenhum sabor escolhido nao ha o que mandar pro carrinho.
      if (this.buyButton) this.buyButton.disabled = current === 0;
    }

    handleStepClick(event) {
      const button = event.target.closest(".tp-kit__step");
      if (!button || !this.contains(button)) return;

      const item = button.closest(".tp-kit__item");
      if (!item) return;

      const step = parseInt(button.dataset.step, 10) || 0;
      const current = parseInt(item.dataset.qty, 10) || 0;

      this.setItemQuantity(item, current + step);
      this.updateCounter();
    }

    // Redistribui na hora do clique, sem esperar o fetch da secao terminar.
    handleOptionChange(event) {
      const target = event.target;
      if (!target?.matches?.(".tp-qty-variants__input")) return;

      if (target.dataset.variantId) this.variantId = target.dataset.variantId;
      this.distribute(this.readTotal());
    }

    // Reconciliacao: a variante tambem muda pelo picker padrao ou por
    // ?variant= na URL. O html que vem no evento ja e a secao renderizada com
    // a variante nova, entao e a leitura mais confiavel.
    subscribeToVariantChange() {
      if (
        typeof subscribe !== "function" ||
        typeof PUB_SUB_EVENTS === "undefined"
      ) {
        return;
      }

      this.variantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.variantChange,
        (event) => {
          const variant = event?.data?.variant;
          if (!variant) return;

          this.variantId = variant.id;
          this.dataset.variantId = variant.id;

          // A troca de tamanho vem por aqui (as pills do tema nao passam pelo
          // handleOptionChange). Precisa vir antes do distribute, senao o
          // clamp usaria o estoque da variante antiga.
          this.applySize(variant);

          this.distribute(
            this.readTotal({ root: event.data.html, variantId: variant.id }),
          );
        },
      );
    }

    // Cada sabor escolhido vira uma linha propria do carrinho. A variante da
    // caixa NAO e adicionada: ela so define quantas unidades distribuir. O
    // desconto do combo tem que vir de um desconto automatico por quantidade.
    async addToCart() {
      if (!this.buyButton || this.buyButton.disabled) return;

      const items = this.items
        .map((item) => ({
          id: item.dataset.variantId,
          quantity: parseInt(item.dataset.qty, 10) || 0,
        }))
        .filter((line) => line.id && line.quantity > 0);

      if (!items.length) return;

      const defaultText =
        this.buyButton.dataset.defaultText || this.buyButton.textContent;

      this.showError("");

      try {
        this.buyButton.disabled = true;
        this.buyButton.textContent = "Adicionando...";

        const base =
          typeof routes !== "undefined" && routes.cart_add_url
            ? routes.cart_add_url
            : "/cart/add";

        const response = await fetch(`${base}.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items }),
        });

        const payload = await response.json().catch(() => null);

        // A Shopify responde 422 com `description` quando algum sabor nao tem
        // estoque para a quantidade pedida. Mostrar isso e melhor que um
        // "tente novamente" generico.
        if (!response.ok) {
          throw new Error(
            payload?.description || payload?.message || "Erro ao adicionar ao carrinho",
          );
        }

        this.buyButton.textContent = "✓ Adicionado!";
        setTimeout(() => this.restoreButton(defaultText), 2000);
        setTimeout(() => this.openCartDrawer(), 300);
      } catch (err) {
        console.error(err);
        this.showError(err.message);
        this.buyButton.textContent = "Erro - tente novamente";
        setTimeout(() => this.restoreButton(defaultText), 2500);
      }
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || "";
      this.errorEl.hidden = !message;
    }

    restoreButton(text) {
      this.buyButton.textContent = text;
      this.buyButton.disabled = this.currentTotal() === 0;
    }

    async openCartDrawer() {
      const drawer = document.querySelector("cart-drawer");
      if (!drawer) {
        window.location.href = routes?.cart_url || "/cart";
        return;
      }

      const response = await fetch("/?sections=cart-drawer,cart-icon-bubble");
      const sections = await response.json();

      drawer.classList.remove("is-empty");
      drawer.renderContents({ sections });

      requestAnimationFrame(() => {
        drawer.classList.add("animate", "active");
        document.body.classList.add("overflow-hidden");
      });
    }
  }

  customElements.define("tp-kit-items", TpKitItems);
}
